import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { resolvePlayerCardStats } from '../lib/playerStatFields';
import { 
  Plus, 
  Search, 
  User,
  AlertCircle,
  Flame,
  FileDown,
  Loader2,
  FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = [
  'Goalkeeper',
  'Right Back',
  'Left Back',
  'Center Back',
  'Defensive Midfielder',
  'Central Midfielder',
  'Attacking Midfielder',
  'Right Wing',
  'Left Wing',
  'Striker',
  'Forward'
];

export default function Players() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const seasonId = user?.current_season_id;
  // `players === null` means "not yet fetched" → loading.
  // When there's no season the list is empty straight away, so no effect runs.
  const [players, setPlayers] = useState(() => (seasonId ? null : []));
  const [inFormTrends, setInFormTrends] = useState({});
  const loading = players === null;
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortMode, setSortMode] = useState('default');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    jersey_number: '',
    status: 'available'
  });

  const PLAYER_STATUSES = [
    { value: 'available', label: 'Available', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'unavailable', label: 'Unavailable', color: 'bg-gray-100 text-gray-700' },
    { value: 'injured', label: 'Injured', color: 'bg-red-100 text-red-700' },
    { value: 'suspended', label: 'Suspended', color: 'bg-amber-100 text-amber-700' },
    { value: 'transferred', label: 'Transferred', color: 'bg-blue-100 text-blue-700' }
  ];

  const STATUS_FILTER_OPTIONS = [
    { value: 'all', label: 'Show All Players' },
    { value: 'available', label: 'Available' },
    { value: 'unavailable', label: 'Unavailable' },
    { value: 'injured', label: 'Injured' },
    { value: 'suspended', label: 'Suspended' },
    { value: 'transferred', label: 'Transferred' }
  ];

  useEffect(() => {
    if (seasonId) {
      fetchPlayers();
    }
  }, [seasonId]);

  const fetchPlayers = async () => {
    try {
      const [playersRes, trendsRes] = await Promise.all([
        axios.get(`${API}/players`),
        axios.get(`${API}/players/in-form`).catch(() => ({ data: { trends: {} } })),
      ]);
      setPlayers(playersRes.data);
      setInFormTrends(trendsRes.data?.trends || {});
    } catch (error) {
      console.error('Failed to fetch players:', error);
      setPlayers([]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error('Please enter a player name');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        position: formData.position || null,
        jersey_number: formData.jersey_number ? parseInt(formData.jersey_number) : null,
        status: formData.status || 'available'
      };

      if (editingPlayer) {
        await axios.put(`${API}/players/${editingPlayer.id}`, payload);
        toast.success('Player updated successfully');
      } else {
        await axios.post(`${API}/players`, payload);
        toast.success('Player added successfully');
      }
      const scrollY = window.scrollY;
      await fetchPlayers();
      resetForm();
      window.scrollTo(0, scrollY);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save player');
    }
  };

  const handlePlayerClick = (playerId) => {
    navigate(`/players/${playerId}`);
  };

  // PDF downloads — squad summary + per-player report.
  const [downloadingSquad, setDownloadingSquad] = useState(false);
  const [downloadingPlayerId, setDownloadingPlayerId] = useState(null);

  const downloadPdf = async (url, fallbackName) => {
    const res = await axios.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const downloadSquadPdf = async () => {
    if (!user?.current_season_id || downloadingSquad) return;
    setDownloadingSquad(true);
    try {
      await downloadPdf(
        `${API}/reports/squad/${user.current_season_id}/pdf`,
        'squad_report.pdf',
      );
      toast.success('Squad report downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not generate squad PDF');
    } finally {
      setDownloadingSquad(false);
    }
  };

  const downloadPlayerPdf = async (player) => {
    if (downloadingPlayerId) return;
    setDownloadingPlayerId(player.id);
    try {
      await downloadPdf(
        `${API}/reports/player/${player.id}/pdf`,
        `${(player.name || 'player').replace(/\s+/g, '_')}_report.pdf`,
      );
      toast.success(`${player.name}'s report downloaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not generate player PDF');
    } finally {
      setDownloadingPlayerId(null);
    }
  };

  const quickChangeStatus = async (playerId, newStatus) => {
    try {
      const res = await axios.patch(`${API}/players/${playerId}/status`, { status: newStatus });
      setPlayers((prev) => prev.map((p) => (p.id === playerId ? res.data : p)));
      toast.success('Status updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      position: '',
      jersey_number: '',
      status: 'available'
    });
    setEditingPlayer(null);
    setDialogOpen(false);
  };

  const getStatusStyle = (status) => {
    const statusObj = PLAYER_STATUSES.find(s => s.value === status);
    return statusObj ? statusObj.color : 'bg-gray-100 text-gray-700';
  };

  // Priority: available first, then alphabetical within each status group
  const STATUS_SORT_ORDER = { available: 0, unavailable: 1, injured: 2, suspended: 3, transferred: 4 };
  // "Form score": stronger of goals_delta + assists_delta weight; players
  // without a trend (didn't hit ≥6 apps) drop to the bottom of the form list.
  const formScore = (playerId) => {
    const t = inFormTrends[playerId];
    if (!t) return -Infinity;
    return (t.goals_delta || 0) * 2 + (t.assists_delta || 0);
  };
  const filteredPlayers = (players || [])
    .filter(player => {
      const matchesSearch = player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (player.position && player.position.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesStatus = statusFilter === 'all' || (player.status || 'available') === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      // Stat-driven sort modes — rank by chosen metric desc, then by name asc on ties.
      const statSortFields = {
        goals: (p) => p.stats?.goals || 0,
        appearances: (p) => (p.stats?.starts || 0) + (p.stats?.sub_appearances || 0),
        starts: (p) => p.stats?.starts || 0,
        minutes: (p) => p.stats?.minutes_played || 0,
      };
      if (statSortFields[sortMode]) {
        const fn = statSortFields[sortMode];
        const sA = fn(a);
        const sB = fn(b);
        if (sA !== sB) return sB - sA;
        return (a.name || '').localeCompare(b.name || '');
      }

      if (sortMode === 'form') {
        const sA = formScore(a.id);
        const sB = formScore(b.id);
        if (sA !== sB) return sB - sA;
        // Fall through to name when tied (or both have no trend)
        return (a.name || '').localeCompare(b.name || '');
      }
      const rankA = STATUS_SORT_ORDER[a.status || 'available'] ?? 99;
      const rankB = STATUS_SORT_ORDER[b.status || 'available'] ?? 99;
      if (rankA !== rankB) return rankA - rankB;
      const minutesA = a.stats?.minutes_played || 0;
      const minutesB = b.stats?.minutes_played || 0;
      if (minutesA !== minutesB) return minutesB - minutesA;
      return (a.name || '').localeCompare(b.name || '');
    });

  if (!user?.current_season_id) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="no-season-message">
          <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 font-title">No Season Selected</h2>
          <p className="text-gray-500 mb-6">Create a season first to manage players.</p>
          <Button 
            onClick={() => navigate('/seasons')}
            className="btn-primary rounded-xl"
          >
            Go to Seasons
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="players-container">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">
            Players
          </h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => downloadSquadPdf()}
              disabled={!user?.current_season_id || downloadingSquad}
              className="rounded-xl shrink-0 bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"
              title="Download squad stats PDF"
              data-testid="download-squad-pdf-btn"
            >
              {downloadingSquad
                ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                : <FileDown className="w-4 h-4" strokeWidth={1.5} />}
            </Button>
            {isAdmin && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              if (!open) {
                resetForm();
              } else {
                setDialogOpen(true);
              }
            }}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  size="icon"
                  onClick={() => resetForm()}
                  className="rounded-xl shrink-0 bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"
                  data-testid="add-player-btn"
                >
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md" onCloseAutoFocus={(e) => e.preventDefault()}>
                <DialogHeader>
                  <DialogTitle className="text-2xl font-bold font-title">
                    {editingPlayer ? 'Edit Player' : 'Add New Player'}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    {editingPlayer ? 'Edit player details' : 'Add a new player to your squad'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Player name"
                      className="rounded-xl"
                      data-testid="player-name-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="position">Position</Label>
                    <Select
                      value={formData.position}
                      onValueChange={(value) => setFormData({ ...formData, position: value })}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="player-position-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {POSITIONS.map((pos) => (
                          <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="jersey_number">Jersey #</Label>
                    <Input
                      id="jersey_number"
                      type="number"
                      min="1"
                      max="99"
                      value={formData.jersey_number}
                      onChange={(e) => setFormData({ ...formData, jersey_number: e.target.value })}
                      placeholder="1"
                      className="rounded-xl"
                      data-testid="player-jersey-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="player-status-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {PLAYER_STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                    className="flex-1 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#064E3B] hover:bg-[#065f46] text-white rounded-xl"
                    data-testid="save-player-btn"
                  >
                    {editingPlayer ? 'Update' : 'Add'} Player
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" strokeWidth={1.5} />
            <Input
              placeholder="Search players..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
              data-testid="search-players-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 rounded-xl" data-testid="status-filter-select">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortMode} onValueChange={setSortMode}>
            <SelectTrigger className="w-full sm:w-44 rounded-xl" data-testid="sort-mode-select">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default" data-testid="sort-default">Sort: Default</SelectItem>
              <SelectItem value="form" data-testid="sort-form">Sort: By Form</SelectItem>
              <SelectItem value="goals" data-testid="sort-goals">Sort: Most Goals</SelectItem>
              <SelectItem value="appearances" data-testid="sort-appearances">Sort: Most Appearances</SelectItem>
              <SelectItem value="starts" data-testid="sort-starts">Sort: Most Starts</SelectItem>
              <SelectItem value="minutes" data-testid="sort-minutes">Sort: Most Minutes</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Players List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading players...</div>
        ) : filteredPlayers.length === 0 ? (
          <div className="text-center py-16">
            <div className="empty-state-icon">
              <User className="w-10 h-10" strokeWidth={1.5} />
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No Players Found</h3>
            <p className="text-gray-500 mb-6 text-sm sm:text-base">
              {searchQuery || statusFilter !== 'all' ? 'Try adjusting your search or filter' : 'Start by adding your first player'}
            </p>
            {isAdmin && !searchQuery && statusFilter === 'all' && (
              <Button
                onClick={() => setDialogOpen(true)}
                className="btn-primary rounded-xl"
              >
                <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Add Player
              </Button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {filteredPlayers.map((player, index) => (
              <Card 
                key={player.id} 
                className="player-card group cursor-pointer opacity-0 animate-fadeInRow" 
                style={{ animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
                onClick={() => handlePlayerClick(player.id)}
                data-testid={`player-card-${player.id}`}
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-3">
                    <h3 className="min-w-0 font-semibold text-gray-900 truncate text-sm sm:text-base">
                      {player.name}
                    </h3>
                    {inFormTrends[player.id]?.in_form && (
                      <span
                        data-testid={`in-form-badge-${player.id}`}
                        title={`Last ${inFormTrends[player.id].window} apps vs previous ${inFormTrends[player.id].window} · +${inFormTrends[player.id].goals_delta}G / +${inFormTrends[player.id].assists_delta}A`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-semibold bg-orange-100 text-orange-700 shrink-0"
                      >
                        <Flame className="w-3 h-3" strokeWidth={2.5} />
                        In Form
                      </span>
                    )}
                    <div className="flex-1" />
                    {isAdmin ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                            className={`text-xs px-2 py-0.5 rounded-full shrink-0 font-medium hover:ring-2 hover:ring-offset-1 hover:ring-emerald-200 transition-shadow ${getStatusStyle(player.status || 'available')}`}
                            data-testid={`player-status-chip-${player.id}`}
                          >
                            {PLAYER_STATUSES.find(s => s.value === (player.status || 'available'))?.label}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`player-status-menu-${player.id}`}
                        >
                          {PLAYER_STATUSES.map((s) => (
                            <DropdownMenuItem
                              key={s.value}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (s.value !== (player.status || 'available')) {
                                  quickChangeStatus(player.id, s.value);
                                }
                              }}
                              className="cursor-pointer"
                              data-testid={`player-status-option-${player.id}-${s.value}`}
                            >
                              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${s.color.split(' ')[0]}`} />
                              {s.label}
                              {s.value === (player.status || 'available') && (
                                <span className="ml-auto text-xs text-gray-400">• current</span>
                              )}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${getStatusStyle(player.status || 'available')}`}>
                        {PLAYER_STATUSES.find(s => s.value === (player.status || 'available'))?.label}
                      </span>
                    )}
                    {/* Per-player PDF download */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); downloadPlayerPdf(player); }}
                      disabled={downloadingPlayerId === player.id}
                      className="text-gray-400 hover:text-emerald-600 transition-colors p-1 rounded shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Download player report PDF"
                      data-testid={`download-player-pdf-${player.id}`}
                      aria-label={`Download ${player.name} report`}
                    >
                      {downloadingPlayerId === player.id
                        ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                        : <FileText className="w-4 h-4" strokeWidth={1.5} />}
                    </button>
                  </div>

                  {/* Stats — coach-configurable via Settings → Player Card Stats. */}
                  {(() => {
                    const statFields = resolvePlayerCardStats(user?.player_card_stats);
                    if (!statFields.length) return null;
                    // Pick a sensible column count: 4 for ≤4 stats, 5 for 5/10, otherwise wrap on small screens.
                    const cols = statFields.length <= 4 ? 4 : Math.min(statFields.length, 5);
                    return (
                      <div
                        className="mt-3 grid gap-2"
                        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
                        data-testid={`player-stats-${player.id}`}
                      >
                        {statFields.map((f) => (
                          <div key={f.key} className="text-center p-2 bg-gray-50 rounded-lg" data-testid={`player-stat-${player.id}-${f.key}`}>
                            <p className={`text-base sm:text-lg font-bold ${f.color}`}>{f.getValue(player)}</p>
                            <p className="text-xs text-gray-500">{f.label}</p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
